import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import {
  HM_MAX_TIMED_MS,
  type HmActionItem,
  type HmOpeningCard,
  type HmTimedAction,
} from '@nest-recruit/shared';
import { apiGet, apiPost, type ApiError } from '../../../lib/api-client';
import { HM_ACTIONS_KEY, HM_OPENINGS_KEY } from './requisitions-api';

// S10: HM-01 and HM-02. Their keys live beside the S9 ones (requisitions-api.ts),
// under the `['requisitions']` root the API's realtime hints name, so an approver
// deciding elsewhere refreshes a manager's phone without a reload (non-negotiable #12).

export function useHmOpenings(): UseQueryResult<readonly HmOpeningCard[], ApiError> {
  return useQuery<readonly HmOpeningCard[], ApiError>({
    queryKey: HM_OPENINGS_KEY,
    queryFn: () => apiGet<readonly HmOpeningCard[]>('/hm/openings'),
    placeholderData: (previous) => previous,
  });
}

export function useHmActions(): UseQueryResult<readonly HmActionItem[], ApiError> {
  return useQuery<readonly HmActionItem[], ApiError>({
    queryKey: HM_ACTIONS_KEY,
    queryFn: () => apiGet<readonly HmActionItem[]>('/hm/actions'),
    placeholderData: (previous) => previous,
  });
}

/**
 * Master plan S10: "log time-to-complete per action type". The clock starts when
 * the component showing the action mounts — the card on HM-02, the HM-03 form — and
 * stops when the server accepts it; the figure goes to the event log, where S29
 * reads it. `start()` restarts it, for a screen that stays up between actions.
 *
 * A timing that fails to record must never disturb the person who just finished
 * their task, so its failure stays on the mutation rather than becoming a toast.
 */
export function useActionTimer(): {
  readonly start: () => void;
  readonly finish: (action: HmTimedAction, requisitionId: string) => void;
} {
  const startedAt = useRef<number | null>(null);
  useEffect(() => {
    startedAt.current = performance.now();
  }, []);
  const record = useMutation<unknown, ApiError, { action: HmTimedAction; requisitionId: string; durationMs: number }>({
    mutationFn: (input) => apiPost('/hm/timings', input),
  });

  return {
    start: () => {
      startedAt.current = performance.now();
    },
    finish: (action, requisitionId) => {
      if (startedAt.current === null) return;
      const durationMs = Math.round(performance.now() - startedAt.current);
      startedAt.current = null;
      // A tab left open overnight is not a measurement; the server refuses it too.
      record.mutate({ action, requisitionId, durationMs: Math.min(durationMs, HM_MAX_TIMED_MS) });
    },
  };
}
